import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const storageKey = "omnimark_connections_v1";
const connectionOauthStateKey = "omnimark_oauth_connection_state";

function decodeState(value) {
  if (!value) return null;
  try {
    return JSON.parse(atob(decodeURIComponent(value)));
  } catch {
    try {
      return JSON.parse(atob(value));
    } catch {
      return null;
    }
  }
}

function loadConnections() {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveOauthResult(connector, result) {
  const records = loadConnections();
  const existing = records[connector] || {
    name: connector,
    status: "setup",
    credentials: {},
    notes: "",
  };
  const notes = [
    existing.notes || "",
    result.error
      ? `OAuth returned an error: ${result.error}`
      : "OAuth sign-in returned to OmniMark, but this browser-only return cannot connect a provider account.",
  ].filter(Boolean).join("\n");

  records[connector] = {
    ...existing,
    // Receiving an authorization code in the browser is not a completed
    // provider connection. OmniMark must exchange it server-side, retain it
    // securely, and pass a provider API test before live data or publishing.
    status: result.error ? "needs_keys" : "verification_required",
    notes,
    last_test: result.error
      ? "Provider returned an OAuth error."
      : "OAuth code returned, but it was not stored or exchanged. Configure the provider's server-side OAuth app first.",
    oauth: {
      code_received: false,
      error: result.error || "",
      state: result.state || "",
      returned_at: new Date().toISOString(),
    },
    updated_at: new Date().toISOString(),
  };
  localStorage.setItem(storageKey, JSON.stringify(records));
  localStorage.setItem("omnimark_connections_return_message", result.error
    ? `${connector} sign-in returned an error. Check provider permissions and try again.`
    : `${connector} returned to OmniMark, but OmniMark did not store or exchange the browser code. Configure secure server-side OAuth before retrying; publishing remains disabled.`);
}

export default function ConnectionsCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Finishing connection...");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    const stateValue = params.get("state") || "";
    const decoded = decodeState(stateValue);
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(connectionOauthStateKey) || "null");
    } catch {
      stored = null;
    }

    const connector = decoded?.connector || stored?.connector || "Third-party connection";
    const code = params.get("code") || "";
    const error = params.get("error") || params.get("error_description") || "";

    saveOauthResult(connector, { code, error, state: stateValue });
    setStatus(error ? `${connector} returned an error.` : `${connector} returned to OmniMark.`);

    const timer = window.setTimeout(() => {
      navigate("/app/connections", { replace: true });
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [navigate, params]);

  return (
    <div style={page} data-testid="connections-callback-page">
      <section style={panel}>
        <div style={eyebrow}>Connection callback</div>
        <h1 style={title}>{status}</h1>
        <p style={copy}>
          OmniMark did not save the returned authorization code. You will be sent back to Connections to review the provider’s secure server-side setup requirements.
        </p>
        <Link to="/app/connections" style={button}>Back to Connections</Link>
      </section>
    </div>
  );
}

const page = { maxWidth: 760, margin: "0 auto", display: "grid", placeItems: "center", minHeight: "60vh", padding: 18 };
const panel = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 22, display: "grid", gap: 10, boxShadow: "0 18px 50px rgba(15,23,42,.08)" };
const eyebrow = { color: "#0f766e", fontWeight: 900, fontSize: 12, textTransform: "uppercase", letterSpacing: 0 };
const title = { margin: 0, fontSize: 30, lineHeight: 1.1 };
const copy = { color: "#475467", margin: 0, lineHeight: 1.5 };
const button = { justifySelf: "start", borderRadius: 8, background: "#111827", color: "#fff", padding: "10px 12px", fontWeight: 900, textDecoration: "none" };
